import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Category, Prompt, SortOption, GeoMetadata, GeoFilters } from "@/lib/types";
import { getEmbeddedPublicProfile } from "@/utils/public-profiles";
import { toast } from "sonner";

const PAGE_SIZE = 10;

export const useInfinitePrompts = (userId?: string) => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [category, setCategory] = useState<Category>("all");
  const [sort, setSort] = useState<SortOption>("latest");
  const [geoFilters, setGeoFilters] = useState<GeoFilters>({});

  const fetchPrompts = useCallback(async (pageNum: number, reset: boolean = false) => {
    if (reset) {
      setLoading(true);
      setPage(0);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }

    try {
      let query = supabase
        .from('prompts')
        .select(`
          *,
          public_profiles!prompts_user_id_fkey(username, avatar_id)
        `);

      // Apply category filter
      if (category !== "all") {
        query = query.eq('category', category);
      }

      // Apply GEO filters
      if (geoFilters.intent) {
        query = query.filter('geo_metadata->>intent', 'eq', geoFilters.intent);
      }
      if (geoFilters.useCase) {
        query = query.filter('geo_metadata->>use_case', 'eq', geoFilters.useCase);
      }

      // Apply sorting
      switch (sort) {
        case "latest":
        case "newest":
          query = query.order('created_at', { ascending: false });
          break;
        case "following":
          if (userId) {
            const { data: followingData } = await supabase
              .from('user_follows')
              .select('following_id')
              .eq('follower_id', userId);
            
            const followingIds = followingData?.map(f => f.following_id) || [];
            if (followingIds.length > 0) {
              query = query.in('user_id', followingIds);
            } else {
              setPrompts([]);
              setHasMore(false);
              setLoading(false);
              setLoadingMore(false);
              return;
            }
          }
          query = query.order('created_at', { ascending: false });
          break;
        default:
          query = query.order('created_at', { ascending: false });
      }

      // Pagination
      const from = pageNum * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      query = query.range(from, to);

      const { data: promptsData, error: promptsError } = await query;

      // Debug logging (enable via localStorage.debug_authors = "1")
      const debugAuthors = typeof window !== "undefined" && localStorage.getItem("debug_authors") === "1";
      if (debugAuthors) {
        console.log("[useInfinitePrompts] QUERY RESULT:", { promptsData, promptsError });
        console.log("[useInfinitePrompts] FIRST PROMPT:", promptsData?.[0]);
        console.log("[useInfinitePrompts] PROFILE DATA:", promptsData?.[0]?.public_profiles);
      }

      if (promptsError) throw promptsError;

      if (!promptsData || promptsData.length === 0) {
        setHasMore(false);
        if (reset) setPrompts([]);
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      // Check if there are more pages
      if (promptsData.length < PAGE_SIZE) {
        setHasMore(false);
      }

      const promptIds = promptsData.map(p => p.id);

      // Batch fetch counts
      const [commentCounts, likeCounts, userLikes, userSaves] = await Promise.all([
        supabase.from('prompt_comments').select('prompt_id').in('prompt_id', promptIds),
        supabase.from('prompt_likes').select('prompt_id').in('prompt_id', promptIds),
        userId ? supabase.from('prompt_likes').select('prompt_id').in('prompt_id', promptIds).eq('user_id', userId) : Promise.resolve({ data: [] }),
        userId ? supabase.from('saved_prompts').select('prompt_id').in('prompt_id', promptIds).eq('user_id', userId) : Promise.resolve({ data: [] })
      ]);

      // Create lookup maps
      const commentCountMap = new Map<string, number>();
      const likeCountMap = new Map<string, number>();
      const userLikedSet = new Set(userLikes.data?.map(l => l.prompt_id) || []);
      const userSavedSet = new Set(userSaves.data?.map(s => s.prompt_id) || []);

      commentCounts.data?.forEach(c => {
        commentCountMap.set(c.prompt_id, (commentCountMap.get(c.prompt_id) || 0) + 1);
      });
      likeCounts.data?.forEach(l => {
        likeCountMap.set(l.prompt_id, (likeCountMap.get(l.prompt_id) || 0) + 1);
      });

      // Map prompts with details - DEFENSIVE: Normalize all fields
      const newPrompts = promptsData.map((prompt: any) => {
        const profile = getEmbeddedPublicProfile(prompt.public_profiles);
        // DEFENSIVE: Ensure category is never null/undefined
        const promptCategory: Exclude<Category, 'all'> = (prompt.category || 'general-chat') as Exclude<Category, 'all'>;
        const title = prompt.title || (prompt.content ? prompt.content.substring(0, 50) : 'Untitled Prompt');

        // Parse GEO metadata
        const geoMetadata = prompt.geo_metadata as GeoMetadata | null;

        return {
          id: prompt.id,
          title: title,
          content: prompt.content || '',
          description: prompt.description || undefined,
          category: promptCategory,
          categoryEditCount: prompt.category_edit_count || 0,
          userId: prompt.user_id,
          username: profile?.username || 'Anonymous',
          avatarId: profile?.avatar_id || 1,
          createdAt: prompt.created_at || new Date().toISOString(),
          likes: likeCountMap.get(prompt.id) || 0,
          comments: commentCountMap.get(prompt.id) || 0,
          isLiked: userLikedSet.has(prompt.id),
          isSaved: userSavedSet.has(prompt.id),
          isPinned: false,
          isRemix: prompt.is_remix || false,
          remixOf: prompt.remix_of,
          geoMetadata: geoMetadata || undefined,
        };
      });

      // For sorting that requires client-side logic
      if (sort === "popular" || sort === "most_liked") {
        newPrompts.sort((a, b) => b.likes - a.likes);
      } else if (sort === "trending") {
        newPrompts.sort((a, b) => (b.likes + b.comments) - (a.likes + a.comments));
      } else if (sort === "most_commented") {
        newPrompts.sort((a, b) => b.comments - a.comments);
      }

      setPrompts(prev => reset ? newPrompts : [...prev, ...newPrompts]);
    } catch (error: any) {
      console.error('Error fetching prompts:', error);
      console.error('Query details:', { category, sort, userId, page: pageNum });
      const errorMessage = error?.message || 'Unknown error occurred';
      toast.error(`Failed to load prompts: ${errorMessage}`);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userId, category, sort, geoFilters]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPrompts(nextPage, false);
    }
  }, [page, loadingMore, hasMore, fetchPrompts]);

  const refresh = useCallback(() => {
    setPage(0);
    fetchPrompts(0, true);
  }, [fetchPrompts]);

  useEffect(() => {
    refresh();
  }, [category, sort, userId, geoFilters]);

  const updatePrompt = (id: string, updatedPrompt: Partial<Prompt>) => {
    setPrompts(prevPrompts => 
      prevPrompts.map(prompt => 
        prompt.id === id ? { ...prompt, ...updatedPrompt } : prompt
      )
    );
    toast.success("Prompt updated");
  };

  const deletePrompt = (id: string) => {
    setPrompts(prevPrompts => prevPrompts.filter(prompt => prompt.id !== id));
    toast.success("Prompt deleted");
  };

  return {
    prompts,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    refresh,
    updatePrompt,
    deletePrompt,
    setCategory,
    setSort,
    setGeoFilters,
    category,
    sort,
    geoFilters,
  };
};
